/**
 * COUPURE RÉSEAU d'une requête de widget — une relance, puis UN message pour toute la page.
 *
 * « Failed to fetch » est l'erreur que lève `fetch` quand aucune réponse HTTP n'arrive. En
 * production, ces coupures sont BRÈVES et GROUPÉES : 1 183 requêtes coupées en 21 jours sur les
 * dashboards CI et Audit, après 173 ms en médiane, par grappes de jusqu'à 28 (journaux du
 * 07/10/2026). Jusqu'ici, chacune affichait son propre message : une grappe = 28 messages.
 *
 * Règles (appliquées par `Containers/Page/saga`) :
 *  1. une coupure réseau est relancée UNE fois, après `DELAI_RELANCE_MS`, et seulement si la
 *     requête est toujours la dernière demande du widget (`Utils/derniereDemandeWidget`) ;
 *  2. si la relance échoue aussi, le widget passe en erreur et la page affiche UN message,
 *     dédoublonné par son `code` fixe (le réducteur des messages n'en garde qu'un) ;
 *  3. une erreur RENVOYÉE PAR LE SERVEUR (403, 500…) n'est ni relancée ni regroupée : elle dit
 *     autre chose (droits, requête invalide) et garde son traitement d'origine.
 *
 * Une requête de widget est une LECTURE : la rejouer ne modifie rien. La coupure reste visible
 * côté serveur (`request aborted`), donc le diagnostic n'est pas masqué.
 *
 * Module volontairement LÉGER, sans import : testable sans charger le graphe des widgets.
 */

/** Délai avant la relance : assez pour sortir d'une grappe de coupures, assez court pour ne pas se voir. */
export const DELAI_RELANCE_MS = 700

/** Code du message regroupé, et clé de traduction (`back/translations/*.json`). */
export const CODE_MESSAGE_COUPURE = "WIDGET_DATA_NETWORK_ERROR"

export const MESSAGE_COUPURE_DEFAUT =
  "Some dashboard data could not be loaded. Try again or refresh the page."

/**
 * Coupure réseau : `fetch` a échoué SANS réponse HTTP (`TypeError: Failed to fetch`).
 * Une erreur HTTP passe par `Utils/request` (`sendError`) et porte `err.response` ; une
 * annulation volontaire est une `AbortError` (`DOMException`), pas un `TypeError`.
 */
export const estCoupureReseau = err => err instanceof TypeError && !err.response

/**
 * Coupures CONSTATÉES sur l'appel réseau lui-même. La classification se fait là où l'on sait que
 * l'erreur vient de `fetch` (`requeteWidgetAvecUneRelance`), pas au `catch` général de la saga :
 * un `TypeError` levé APRÈS la réponse (corps de forme inattendue, mise en forme des options) est un
 * défaut de code, pas une coupure — il doit garder son message d'origine, pas le message regroupé.
 */
const coupuresConstatees = new WeakSet()

/** Marque l'erreur si c'est une coupure réseau ; rend vrai dans ce cas. */
export const marquerSiCoupure = err => {
  if (!estCoupureReseau(err)) return false
  coupuresConstatees.add(err)
  return true
}

/** L'erreur a-t-elle été constatée comme coupure par l'appel réseau ? */
export const estCoupureConstatee = err => err != null && typeof err === "object" && coupuresConstatees.has(err)
